import { configureStore } from '@reduxjs/toolkit'
import { styled } from '@mui/material/styles'
import { useMemo } from 'react'
import { Provider } from 'react-redux'

import treatmentReducer, { setToothChartSelected } from 'app/slice/treatment.slice'
import ToothChart from 'component/ToothChart/ToothChart'
import { getTdhToothChartPreviewLayout, TreatmentPlanPreviewTooth } from './treatment-plan-ui.utils'

type Props = {
  teeth: TreatmentPlanPreviewTooth[]
  teethType: string
}

const TdhToothChartPreviewFrame = styled('div')(() => {
  const layout = getTdhToothChartPreviewLayout()

  return {
    position: 'relative',
    width: '100%',
    // Keep the chart at the same proportions in the modal and print clone.
    height: `${layout.previewHeight}px`,
    minHeight: `${layout.previewHeight}px`,
    aspectRatio: layout.aspectRatio,
    overflow: 'hidden',
  }
})

const TdhToothChartPreviewCanvas = styled('div')(() => {
  const layout = getTdhToothChartPreviewLayout()
  const quadrantColumns = `repeat(${layout.columnsPerQuadrant}, minmax(0, 1fr))`
  const positionMarkBaseSize = `${layout.positionMarkSize / layout.positionMarkScale}px`

  return {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
    '& > div:first-of-type': {
      display: 'none',
    },
    '& #preview-tooth-chart': {
      display: 'none',
    },
    '& #tooth-chart, & #tooth-chart-child': {
      width: '100%',
      height: '100%',
      maxWidth: 'none',
      display: 'flex',
      flexDirection: 'column',
      zoom: 1,
      pointerEvents: 'none',
    },
    '& #tooth-chart > div, & #tooth-chart-child > div': {
      width: '100%',
      height: '50%',
      minHeight: 0,
      flex: '1 1 50%',
      display: 'flex',
    },
    '& #tooth-chart > div > div, & #tooth-chart-child > div > div': {
      width: '50%',
      height: '100%',
      minWidth: 0,
      minHeight: 0,
      display: 'grid',
      gridTemplateColumns: quadrantColumns,
      padding: '0 !important',
      margin: '0 !important',
      boxSizing: 'border-box',
    },
    '& #tooth-chart > div > div > div, & #tooth-chart-child > div > div > div': {
      width: '100% !important',
      height: '100% !important',
      minWidth: 0,
      minHeight: 0,
      margin: '0 !important',
      padding: '0 !important',
      top: 'auto !important',
      left: 'auto !important',
      display: 'grid',
      gridTemplateRows: layout.toothCellGridRows,
      alignItems: 'center',
      justifyItems: 'center',
      alignContent: 'center',
      justifyContent: 'stretch',
      overflow: 'hidden',
      boxSizing: 'border-box',
    },
    '& #tooth-chart > div > div > div > div, & #tooth-chart-child > div > div > div > div': {
      minWidth: 0,
      minHeight: 0,
      margin: '0 !important',
      padding: '0 !important',
      boxSizing: 'border-box',
    },

    // Keep the tooth, position map and number in stable rows. PositionTeeth's
    // five SVG pieces need their native 32px box to form a complete ring.
    '& #tooth-chart > div:first-child > div > div, & #tooth-chart-child > div:first-child > div > div': {
      paddingBottom: '18px !important',
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 0,
      },
      '& > div:first-child': {
        gridRow: 1,
        width: '100% !important',
        height: '100% !important',
        minHeight: 0,
        overflow: 'hidden',
      },
      '& > div:nth-child(2)': {
        gridRow: 2,
        display: 'block',
        flex: '0 0 auto',
        width: `${positionMarkBaseSize} !important`,
        height: `${positionMarkBaseSize} !important`,
        transform: `scale(${layout.positionMarkScale})`,
        transformOrigin: 'center',
      },
      '& > p, & > .MuiTypography-root': {
        gridRow: 3,
        flex: '0 0 auto',
      },
    },
    '& #tooth-chart > div:last-child > div > div, & #tooth-chart-child > div:last-child > div > div': {
      gridTemplateRows: `36px ${positionMarkBaseSize} minmax(0, 1fr)`,
      paddingTop: '18px !important',
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 0,
      },
      '& > p, & > .MuiTypography-root': {
        gridRow: 1,
        flex: '0 0 auto',
      },
      '& > div:nth-child(2)': {
        gridRow: 2,
        display: 'block',
        flex: '0 0 auto',
        width: `${positionMarkBaseSize} !important`,
        height: `${positionMarkBaseSize} !important`,
        transform: `scale(${layout.positionMarkScale})`,
        transformOrigin: 'center',
      },
      '& > div:last-child': {
        gridRow: 3,
        width: '100% !important',
        height: '100% !important',
        flex: '0 0 auto',
        minHeight: 0,
        overflow: 'hidden',
      },
    },
    '& #tooth-chart [class*="div-teeth-"], & #tooth-chart-child [class*="div-teeth-"]': {
      width: '100% !important',
      height: '100% !important',
      minHeight: 0,
      margin: '0 !important',
      padding: '0 1px !important',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      boxSizing: 'border-box',
    },
    '& #tooth-chart [class*="div-teeth-"] > svg, & #tooth-chart [class*="div-teeth-"] > img, & #tooth-chart-child [class*="div-teeth-"] > svg, & #tooth-chart-child [class*="div-teeth-"] > img': {
      display: 'block',
      width: 'auto !important',
      height: '100% !important',
      maxWidth: '100% !important',
      maxHeight: '100% !important',
      flex: '0 0 auto',
    },
    '& #tooth-chart .MuiTypography-root, & #tooth-chart-child .MuiTypography-root': {
      flex: '0 0 auto',
      margin: '0 !important',
      fontSize: `${layout.labelFontSize}px !important`,
      lineHeight: '1 !important',
    },
    '& #tooth-chart > div:last-child > div::after, & #tooth-chart-child > div:last-child > div::after': {
      top: '0px',
      bottom: 'auto',
      fontSize: '12px',
      lineHeight: 1,
      backgroundColor: '#fff',
      padding: '0 2px',
      zIndex: 2,
    },
    '& .MuiButtonBase-root': {
      pointerEvents: 'none',
    },
  }
})

export default function TdhToothChartPreview({ teeth, teethType }: Props) {
  const previewStore = useMemo(() => {
    const store = configureStore({
      reducer: { treatment: treatmentReducer },
    })

    // Feed the chart through its history path. That path renders the saved
    // operative and surface positions without turning the preview into an
    // editable selection.
    store.dispatch(setToothChartSelected([
      teeth.map((tooth) => ({ ...tooth, isSelect: true })),
    ]))

    return store
  }, [teeth])

  return (
    <TdhToothChartPreviewFrame>
      <TdhToothChartPreviewCanvas>
        <Provider store={previewStore}>
          <ToothChart
            type="treatment"
            teethType={teethType}
            patientId={0}
            noneSave
            readOnly
          />
        </Provider>
      </TdhToothChartPreviewCanvas>
    </TdhToothChartPreviewFrame>
  )
}
